import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReduxState } from "..";
import { getXeroItems, createXeroItem } from "../../docuclipper/api";

export interface XeroItem {
  name: string;
  value: string;
  code?: string;
  description?: string;
}

const initialState: {
  xeroItems: XeroItem[];
  loading: boolean;
  creating: boolean;
  error: string | null;
} = {
  xeroItems: [],
  loading: false,
  creating: false,
  error: null,
};

const slice = createSlice({
  name: "XeroItem",
  initialState,
  reducers: {
    load(state) {
      state.loading = true;
      state.error = null;
    },
    setData(state, action: PayloadAction<XeroItem[]>) {
      state.loading = false;
      state.xeroItems = action.payload;
    },
    setError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    setCreating(state, action: PayloadAction<boolean>) {
      state.creating = action.payload;
    },
    addXeroItem(state, action: PayloadAction<XeroItem>) {
      state.xeroItems.unshift(action.payload);
      state.creating = false;
    },
  },
});

export const { load, setError, setData, setCreating, addXeroItem } = slice.actions;

export const fetchXeroItems = (integrationId: number, search?: string) => async (dispatch, getState) => {
  const state: ReduxState = getState();
  if (state.XeroItem.loading) {
    return;
  }
  dispatch(load());
  try {
    const items = await getXeroItems(integrationId, search);
    dispatch(
      setData(
        (items || []).map((i: any) => ({
          // Xero API returns lowercase field names
          name: i.name || i.Name,
          value: i.itemID || i.ItemID,
          code: i.code || i.Code,
          description: i.description || i.Description,
        }))
      )
    );
  } catch (err) {
    dispatch(setError((err as any).message || "Error getting Xero items"));
  }
};

export const createAndAddXeroItem = (integrationId: number, itemName: string) => async (dispatch, getState) => {
  dispatch(setCreating(true));
  try {
    const item = await createXeroItem(integrationId, itemName);
    const newItem: XeroItem = {
      name: item.name || item.Name,
      value: item.itemID || item.ItemID,
      code: item.code || item.Code,
      description: item.description || item.Description,
    };
    dispatch(addXeroItem(newItem));
    return newItem;
  } catch (err) {
    dispatch(setError((err as any).message || "Error creating Xero item"));
    throw err;
  } finally {
    dispatch(setCreating(false));
  }
};

export default slice.reducer;

